var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"J2z0V9_BrfkK9TfiyiJOb"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "/saatchi-general";

import * as Sentry from '@sentry/nextjs'
import { getCriticalPageInfo } from './lib-ts/sentry/criticalPages'

const { APP_ENV, NODE_ENV } = process.env
const isProd = NODE_ENV === 'production' || APP_ENV === 'production'

// Only initialize Sentry in production environment
if (isProd) {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

    // Environment tag for better filtering in Sentry
    environment: APP_ENV || 'unknown',

    // Sample rate for error events (1.0 = 100%)
    // Since we're filtering by page in beforeSend, we keep this at 1.0
    sampleRate: 1.0,

    // Performance Monitoring
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    tracesSampleRate: 0.1, // 10% of transactions

    tracesSampler: (samplingContext) => {
      // Sample critical pages at a higher rate
      const pathname = samplingContext.location?.pathname || ''
      const pageInfo = getCriticalPageInfo(pathname)
      if (pageInfo) {
        return 0.25 // 25% for critical pages
      }
      return 0.05 // 5% for other pages
    },

    replaysSessionSampleRate: 0.0, // Disabled for now

    replaysOnErrorSampleRate: 0.0, // Disabled for now

    integrations: [
      new Sentry.BrowserTracing({
        // Set tracePropagationTargets to control which URLs distributed tracing should be enabled for
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/www\.saatchiart\.com/,
          /^\/api/,
        ],
      }),
      // Replay integration is disabled by default, uncomment to enable
      // new Sentry.Replay(),
    ],

    // Custom filtering: Only send events from critical pages
    beforeSend(event, hint) {
      // Get the current pathname
      const pathname = window?.location?.pathname || ''

      // Get critical page info (returns page name if critical, null otherwise)
      const pageInfo = getCriticalPageInfo(pathname)
      
      // Only send events if the current page is critical
      if (!pageInfo) {
        // Drop the event - don't send to Sentry
        return null
      }

      // Add custom tags to help with filtering in Sentry
      event.tags = {
        ...event.tags,
        critical_page: true,
        page_name: pageInfo,
      }

      // Add pathname to context
      event.contexts = {
        ...event.contexts,
        page: {
          pathname,
          url: window?.location?.href || '',
        },
      }

      return event
    },

    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      // Generic error that doesn't provide useful context
      'Script error.',
      'UnhandledRejection',
      // Browser extensions
      'chrome-extension',
      'moz-extension',
      // Network errors that aren't actionable
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      'ResizeObserver loop',
    ],

    // Ignore certain URLs (usually browser extensions or third-party scripts)
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    ],
  })
}

